import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  collection,
  doc,
  documentId,
  getDocs,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where
} from 'firebase/firestore'
import Card from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import Icon from '../../../components/AppIcon'
import { db } from '../../../firebase'
import { useToast } from '../../../lib/ToastContext'
import { BRAND_VENDOR } from '../../../config/deploy'

const FIELD_LABELS = {
  product_type: 'Product Type',
  brand: 'Brand',
  material: 'Material',
  gender: 'Gender',
  age_group: 'Age Group',
  color: 'Color',
  size: 'Size',
  weight: 'Weight',
  weight_unit: 'Weight Unit',
  condition: 'Condition',
  gtin: 'GTIN',
  mpn: 'MPN'
}

const SHOPIFY_BATCH_SIZE = 10

function chunkArray(items, size) {
  const chunks = []
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size))
  }
  return chunks
}

function normalizeText(value) {
  return String(value || '').trim()
}

function normalizeDigits(value) {
  return String(value || '').replace(/\D/g, '')
}

function toMillis(value) {
  if (!value) return 0
  if (typeof value?.toMillis === 'function') return value.toMillis()
  if (typeof value?.toDate === 'function') return value.toDate().getTime()
  const parsed = Date.parse(String(value))
  return Number.isFinite(parsed) ? parsed : 0
}

function normalizeTags(value) {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeText(item)).filter(Boolean).join(', ')
  }
  return normalizeText(value)
}

function buildMergedProduct(localProduct, shopifyProduct) {
  const localMetafields = localProduct?.metafields && typeof localProduct.metafields === 'object'
    ? localProduct.metafields
    : {}
  const shopifyMetafields = shopifyProduct?.metafields && typeof shopifyProduct.metafields === 'object'
    ? shopifyProduct.metafields
    : {}
  const localVariants = Array.isArray(localProduct?.variants) ? localProduct.variants : []
  const shopifyVariants = Array.isArray(shopifyProduct?.variants) ? shopifyProduct.variants : []

  return {
    ...localProduct,
    ...shopifyProduct,
    localProductId: localProduct?.id || null,
    shopifyId: normalizeText(shopifyProduct?.id || localProduct?.shopifyId),
    productType: normalizeText(shopifyProduct?.productType || localProduct?.productType),
    vendor: normalizeText(shopifyProduct?.vendor || localProduct?.vendor || BRAND_VENDOR),
    bodyHtml: shopifyProduct?.bodyHtml || localProduct?.description || '',
    description: localProduct?.description || shopifyProduct?.bodyHtml || '',
    tags: normalizeTags(shopifyProduct?.tags || localProduct?.tags || ''),
    image: shopifyProduct?.image || localProduct?.image || localProduct?.initialImage || '',
    variants: shopifyVariants.length ? shopifyVariants : localVariants,
    metafields: {
      ...localMetafields,
      ...shopifyMetafields
    }
  }
}

function getPrimaryVariant(product) {
  return product?.variants?.[0] || {}
}

function normalizeCategory(value) {
  const text = normalizeText(value).toLowerCase()
  if (text.includes('ring') || text.includes('band')) return 'rings'
  if (text.includes('pendant')) return 'pendants'
  if (text.includes('necklace')) return 'necklaces'
  if (text.includes('chain')) return 'chains'
  if (text.includes('bracelet') || text.includes('bangle')) return 'bracelets'
  if (text.includes('earring')) return 'earrings'
  if (text.includes('watch')) return 'watches'
  return 'other'
}

function buildEffectiveGoogleFields(product) {
  const metafields = product?.metafields || {}
  const variant = getPrimaryVariant(product)
  const weight = normalizeText(metafields.weight || variant.weight || product?.weight || '')
  const barcode = normalizeDigits(metafields.gtin || variant.barcode || product?.barcode || '')
  const sku = normalizeText(metafields.mpn || variant.sku || product?.sku || '')

  return {
    product_type: normalizeText(metafields.product_type || product?.productType || ''),
    brand: normalizeText(metafields.brand || product?.vendor || ''),
    material: normalizeText(metafields.material || ''),
    gender: normalizeText(metafields.gender || product?.subcategory || ''),
    age_group: normalizeText(metafields.age_group || ''),
    color: normalizeText(metafields.color || ''),
    size: normalizeText(metafields.size || metafields.ring_size || ''),
    weight,
    weight_unit: normalizeText(metafields.weight_unit || (weight ? 'g' : '')),
    condition: normalizeText(metafields.condition || ''),
    gtin: barcode,
    mpn: sku
  }
}

function analyzeGoogleCoverage(product) {
  const current = buildEffectiveGoogleFields(product)
  const category = normalizeCategory(`${product?.productType || ''} ${product?.title || ''}`)
  const blockingKeys = []
  const recommendedKeys = []

  if (!current.product_type) blockingKeys.push('product_type')
  if (!current.brand) blockingKeys.push('brand')
  if (!current.material) blockingKeys.push('material')
  if (!current.condition) blockingKeys.push('condition')
  if (!current.gtin && !current.mpn) {
    blockingKeys.push('gtin')
    blockingKeys.push('mpn')
  }

  if (!current.gender) recommendedKeys.push('gender')
  if (!current.age_group) recommendedKeys.push('age_group')
  if (!current.color) recommendedKeys.push('color')
  if (!current.weight) {
    recommendedKeys.push('weight')
  } else if (!current.weight_unit) {
    recommendedKeys.push('weight_unit')
  }
  if (category === 'rings' && !current.size) {
    recommendedKeys.push('size')
  }

  const toField = (key, severity) => ({
    key,
    label: FIELD_LABELS[key] || key,
    severity
  })

  return {
    blockingFields: blockingKeys.map((key) => toField(key, 'blocking')),
    recommendedFields: recommendedKeys.map((key) => toField(key, 'recommended'))
  }
}

function sortRows(a, b) {
  const blockingDelta = b.analysis.blockingFields.length - a.analysis.blockingFields.length
  if (blockingDelta !== 0) return blockingDelta
  const recommendedDelta = b.analysis.recommendedFields.length - a.analysis.recommendedFields.length
  if (recommendedDelta !== 0) return recommendedDelta
  return toMillis(b.product?.updatedAt || b.localProduct?.updatedAt) - toMillis(a.product?.updatedAt || a.localProduct?.updatedAt)
}

function buildFixPayload(row) {
  const product = row.product
  const variant = getPrimaryVariant(product)
  return {
    title: product?.title || '',
    description: product?.bodyHtml || product?.description || '',
    sellerDescription: row.localProduct?.description || product?.description || '',
    productType: product?.productType || '',
    tags: product?.tags || '',
    vendor: product?.vendor || BRAND_VENDOR,
    subcategory: product?.subcategory || '',
    imageBase64: product?.image || '',
    barcode: variant?.barcode || product?.barcode || '',
    sku: variant?.sku || product?.sku || '',
    weight: variant?.weight || product?.weight || product?.metafields?.weight || '',
    variant,
    metafields: product?.metafields || {},
    missingFields: [
      ...row.analysis.blockingFields,
      ...row.analysis.recommendedFields
    ].map((field) => `metafields.${field.key}`)
  }
}

function formatFieldList(fields) {
  return fields.map((field) => field.label).join(', ')
}

const GoogleFixesTab = ({
  generateGoogleMetadataFix,
  updateProductFields,
  compactViewport = false
}) => {
  const toast = useToast()
  const [onlineProducts, setOnlineProducts] = useState([])
  const [loadingProducts, setLoadingProducts] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [shopifyProductsById, setShopifyProductsById] = useState({})
  const [loadingShopify, setLoadingShopify] = useState(false)
  const [fixingProductId, setFixingProductId] = useState('')
  const [fixStateById, setFixStateById] = useState({})

  useEffect(() => {
    const q = query(
      collection(db, 'products'),
      where('publishedTo', 'array-contains', 'online'),
      limit(300)
    )

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const nextProducts = snapshot.docs
        .map((entry) => ({ id: entry.id, ...entry.data() }))
        .filter((product) => product?.status !== 'sold' && product?.status !== 'archived')
        .sort((a, b) => toMillis(b?.updatedAt || b?.createdAt) - toMillis(a?.updatedAt || a?.createdAt))

      setOnlineProducts(nextProducts)
      setLoadingProducts(false)
      setLoadError('')
    }, (err) => {
      console.error('Failed to load online products:', err)
      setLoadError('Failed to load online products.')
      setLoadingProducts(false)
    })

    return () => unsubscribe()
  }, [])

  useEffect(() => {
    const shopifyIds = Array.from(new Set(
      onlineProducts
        .map((product) => normalizeText(product?.shopifyId))
        .filter(Boolean)
    ))

    if (!shopifyIds.length) {
      setShopifyProductsById({})
      setLoadingShopify(false)
      return undefined
    }

    let cancelled = false
    setLoadingShopify(true)

    const loadShopifyDocs = async () => {
      try {
        const chunks = chunkArray(shopifyIds, SHOPIFY_BATCH_SIZE)
        const snapshots = await Promise.all(chunks.map((ids) => (
          getDocs(query(collection(db, 'shopify_products'), where(documentId(), 'in', ids)))
        )))

        if (cancelled) return

        const nextMap = {}
        snapshots.forEach((snapshot) => {
          snapshot.docs.forEach((entry) => {
            nextMap[entry.id] = {
              id: entry.id,
              ...entry.data()
            }
          })
        })
        setShopifyProductsById(nextMap)
      } catch (err) {
        console.error('Failed to load Shopify cache products:', err)
      } finally {
        if (!cancelled) {
          setLoadingShopify(false)
        }
      }
    }

    loadShopifyDocs()

    return () => {
      cancelled = true
    }
  }, [onlineProducts])

  const queueRows = useMemo(() => {
    const rows = []
    for (const localProduct of onlineProducts) {
      const shopifyId = normalizeText(localProduct?.shopifyId)
      if (!shopifyId) continue
      const shopifyProduct = shopifyProductsById[shopifyId] || null
      const mergedProduct = buildMergedProduct(localProduct, shopifyProduct)
      const analysis = analyzeGoogleCoverage(mergedProduct)
      const totalIssues = analysis.blockingFields.length + analysis.recommendedFields.length
      if (!totalIssues) continue

      rows.push({
        id: localProduct.id,
        shopifyId,
        localProduct,
        shopifyProduct,
        product: mergedProduct,
        analysis
      })
    }

    return rows.sort(sortRows)
  }, [onlineProducts, shopifyProductsById])

  const skippedOnlineCount = useMemo(
    () => onlineProducts.filter((product) => !normalizeText(product?.shopifyId)).length,
    [onlineProducts]
  )

  const summary = useMemo(() => {
    const linkedOnlineCount = onlineProducts.length - skippedOnlineCount
    const blockingProducts = queueRows.filter((row) => row.analysis.blockingFields.length > 0).length
    const recommendedOnly = queueRows.filter((row) => row.analysis.blockingFields.length === 0).length
    return {
      linkedOnlineCount,
      blockingProducts,
      recommendedOnly,
      healthyProducts: Math.max(0, linkedOnlineCount - queueRows.length)
    }
  }, [onlineProducts, queueRows, skippedOnlineCount])

  const handleFix = useCallback(async (row) => {
    if (!generateGoogleMetadataFix) {
      toast.error('Google metadata AI fixes are unavailable right now.')
      return
    }
    if (!updateProductFields) {
      toast.error('Shopify product update is unavailable right now.')
      return
    }

    setFixingProductId(row.id)
    setFixStateById((prev) => ({
      ...prev,
      [row.id]: {
        status: 'working',
        message: ''
      }
    }))

    try {
      const fixResult = await generateGoogleMetadataFix(buildFixPayload(row))
      const nextMetafields = {
        ...(row.product?.metafields || {}),
        ...(fixResult?.metafields || {})
      }
      const nextProductType = normalizeText(fixResult?.productType || row.product?.productType || '')
      const metafieldPatch = fixResult?.metafields && Object.keys(fixResult.metafields).length > 0
        ? fixResult.metafields
        : null

      if (!metafieldPatch && !nextProductType) {
        throw new Error('No Google metadata changes were returned.')
      }

      await updateProductFields(row.shopifyId, {
        ...(nextProductType ? { productType: nextProductType } : {}),
        ...(metafieldPatch ? { metafields: metafieldPatch } : {})
      })

      if (row.localProduct?.id) {
        try {
          await updateDoc(doc(db, 'products', row.localProduct.id), {
            ...(nextProductType ? { productType: nextProductType } : {}),
            metafields: nextMetafields,
            updatedAt: serverTimestamp()
          })
        } catch (localErr) {
          console.warn('Failed to update local product Google metadata cache:', localErr)
        }
      }

      setShopifyProductsById((prev) => ({
        ...prev,
        [row.shopifyId]: {
          ...(prev[row.shopifyId] || row.shopifyProduct || {}),
          id: row.shopifyId,
          productType: nextProductType || row.product?.productType || '',
          metafields: nextMetafields,
          updatedAt: new Date().toISOString()
        }
      }))

      const unresolved = Array.isArray(fixResult?.missingFields) ? fixResult.missingFields : []
      const reviewFlags = Array.isArray(fixResult?.reviewFlags) ? fixResult.reviewFlags : []
      const summaryLine = normalizeText(fixResult?.summary)

      setFixStateById((prev) => ({
        ...prev,
        [row.id]: {
          status: unresolved.length ? 'partial' : 'done',
          message: summaryLine,
          unresolved,
          reviewFlags
        }
      }))

      if (unresolved.length) {
        toast.warning(`Google fix applied, but ${unresolved.length} field${unresolved.length === 1 ? ' is' : 's are'} still missing.`)
      } else {
        toast.success('Google marketplace metadata updated.')
      }
    } catch (err) {
      console.error('Failed to fix Google metadata:', err)
      const message = err?.message || 'Failed to fix Google metadata.'
      setFixStateById((prev) => ({
        ...prev,
        [row.id]: {
          status: 'error',
          message
        }
      }))
      toast.error(message)
    } finally {
      setFixingProductId('')
    }
  }, [generateGoogleMetadataFix, toast, updateProductFields])

  const loading = loadingProducts || loadingShopify

  if (loading && !queueRows.length) {
    return (
      <Card variant="solid" className="border border-border/60 shadow-elevation-1">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <Icon name="Loader2" size={16} className="animate-spin" />
          Loading online Google marketplace issues...
        </div>
      </Card>
    )
  }

  if (loadError) {
    return (
      <Card variant="solid" className="border border-destructive/30 bg-destructive/10 text-destructive shadow-elevation-1">
        <div className="flex items-start gap-3">
          <Icon name="AlertTriangle" size={18} className="mt-0.5" />
          <div>
            <p className="text-sm font-semibold">Google Fixes is unavailable</p>
            <p className="text-sm">{loadError}</p>
          </div>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <div className={compactViewport ? 'grid grid-cols-1 gap-3' : 'grid grid-cols-1 md:grid-cols-4 gap-3'}>
        <Card variant="solid" className="border border-border/60 shadow-elevation-1">
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Online Linked</p>
          <p className="text-2xl font-semibold text-foreground">{summary.linkedOnlineCount}</p>
          <p className="text-sm text-muted-foreground mt-1">Online products with a Shopify link.</p>
        </Card>
        <Card variant="solid" className="border border-destructive/25 shadow-elevation-1">
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Blocking</p>
          <p className="text-2xl font-semibold text-foreground">{summary.blockingProducts}</p>
          <p className="text-sm text-muted-foreground mt-1">Products missing fields that can block marketplace listing.</p>
        </Card>
        <Card variant="solid" className="border border-warning/25 shadow-elevation-1">
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Recommended</p>
          <p className="text-2xl font-semibold text-foreground">{summary.recommendedOnly}</p>
          <p className="text-sm text-muted-foreground mt-1">Products that still need enrichment for stronger Google coverage.</p>
        </Card>
        <Card variant="solid" className="border border-success/25 shadow-elevation-1">
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Healthy</p>
          <p className="text-2xl font-semibold text-foreground">{summary.healthyProducts}</p>
          <p className="text-sm text-muted-foreground mt-1">Online products with no current Google-fix issues.</p>
        </Card>
      </div>

      <Card variant="solid" className="border border-border/60 shadow-elevation-1">
        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
              <Icon name="Search" size={20} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground">Google Marketplace Fixes</h3>
              <p className="text-sm text-muted-foreground">
                Only online products appear here. `Fix It` backfills safe fields first, then asks Claude for the missing Google marketplace data that still needs judgment.
              </p>
            </div>
          </div>
          {skippedOnlineCount > 0 && (
            <div className="rounded-xl border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning-foreground">
              {skippedOnlineCount} online product{skippedOnlineCount === 1 ? '' : 's'} skipped because no Shopify link was found.
            </div>
          )}
        </div>
      </Card>

      {!queueRows.length ? (
        <Card variant="solid" className="border border-success/25 shadow-elevation-1">
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="w-14 h-14 rounded-2xl bg-success/10 text-success flex items-center justify-center mb-4">
              <Icon name="CheckCircle2" size={24} />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">No Google fixes queued</h3>
            <p className="text-sm text-muted-foreground max-w-2xl">
              Online products with a Shopify link currently have the Google marketplace fields this tab checks for.
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {queueRows.map((row) => {
            const variant = getPrimaryVariant(row.product)
            const fixState = fixStateById[row.id] || {}
            const isFixing = fixingProductId === row.id
            const unresolvedFields = Array.isArray(fixState.unresolved)
              ? fixState.unresolved.map((field) => FIELD_LABELS[String(field).replace(/^metafields\./, '')] || field)
              : []
            const reviewFlags = Array.isArray(fixState.reviewFlags) ? fixState.reviewFlags : []

            return (
              <Card
                key={row.id}
                variant="solid"
                className="border border-border/60 shadow-elevation-1"
              >
                <div className={compactViewport ? 'space-y-4' : 'grid grid-cols-[minmax(0,1fr)_auto] gap-4'}>
                  <div className="flex items-start gap-4 min-w-0">
                    <div className="w-20 h-20 rounded-2xl overflow-hidden bg-muted/30 border border-border/60 shrink-0">
                      {row.product?.image ? (
                        <img
                          src={row.product.image}
                          alt={row.product?.title || 'Product'}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground/60">
                          <Icon name="ImageOff" size={20} />
                        </div>
                      )}
                    </div>

                    <div className="min-w-0 flex-1 space-y-3">
                      <div>
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="text-base font-semibold text-foreground truncate">{row.product?.title || 'Untitled product'}</h3>
                          {row.analysis.blockingFields.length > 0 ? (
                            <span className="inline-flex items-center rounded-full bg-destructive/10 px-2 py-0.5 text-[11px] font-medium text-destructive">
                              {row.analysis.blockingFields.length} blocking
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-warning/10 px-2 py-0.5 text-[11px] font-medium text-warning">
                              recommended only
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          <span>Type: {row.product?.productType || 'Not set'}</span>
                          <span>SKU: {variant?.sku || row.product?.sku || 'Missing'}</span>
                          <span>GTIN-8: {variant?.barcode || row.product?.barcode || 'Missing'}</span>
                        </div>
                      </div>

                      {row.analysis.blockingFields.length > 0 && (
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-destructive mb-1">Blocking</p>
                          <p className="text-sm text-foreground">{formatFieldList(row.analysis.blockingFields)}</p>
                        </div>
                      )}

                      {row.analysis.recommendedFields.length > 0 && (
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-warning mb-1">Recommended</p>
                          <p className="text-sm text-foreground">{formatFieldList(row.analysis.recommendedFields)}</p>
                        </div>
                      )}

                      {fixState?.message && (
                        <div className={`rounded-xl border px-3 py-2 text-sm ${
                          fixState.status === 'error'
                            ? 'border-destructive/30 bg-destructive/10 text-destructive'
                            : fixState.status === 'partial'
                              ? 'border-warning/30 bg-warning/10 text-foreground'
                              : 'border-success/30 bg-success/10 text-foreground'
                        }`}>
                          {fixState.message}
                          {unresolvedFields.length > 0 && (
                            <p className="mt-1 text-xs text-muted-foreground">
                              Still missing: {unresolvedFields.join(', ')}
                            </p>
                          )}
                          {reviewFlags.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {reviewFlags.slice(0, 3).map((flag) => (
                                <p key={`${flag.field}-${flag.reason}`} className="text-xs text-muted-foreground">
                                  {flag.label}: {flag.reason}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col items-stretch gap-2 min-w-[190px]">
                    <Button
                      variant="default"
                      onClick={() => handleFix(row)}
                      disabled={isFixing}
                    >
                      <Icon
                        name={isFixing ? 'Loader2' : 'Sparkles'}
                        size={14}
                        className={`mr-2 ${isFixing ? 'animate-spin' : ''}`}
                      />
                      {isFixing ? 'Fixing...' : 'Fix It'}
                    </Button>
                    <div className="rounded-xl border border-border/60 bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
                      Shopify ID: <span className="font-mono text-foreground">{row.shopifyId}</span>
                    </div>
                  </div>
                </div>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default GoogleFixesTab
